import { useState, useEffect } from 'react'
import { employeesAPI } from '../../services/api'
import { 
  UserGroupIcon, 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { t, locale } from '../../i18n'
import CurrencyCode from '../../components/CurrencyCode'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'
import { servicesOf } from '../../config/services'
import { PAGES, ROLE_PAGES, ROLE_PRESETS } from '../../config/permissions'

const emptyForm = () => ({
  username: '',
  password: '',
  confirm_password: '',
  full_name: '',
  role: 'cashier',
  role_choice: 'cashier',
  role_label: '',
  permissions: [],
  pin: '',
  salary: '',
  phone: '',
  email: '',
  hire_date: new Date().toISOString().split('T')[0],
  is_active: true
})

const ROLE_NAMES = { admin: 'Admin', manager: 'Manager', cashier: 'Cashier', kitchen: 'Kitchen', waiter: 'Waiter', owner: 'Owner' }

// Pages that only make sense for some services (e.g. Kitchen for a menu).
const SERVICE_PAGES = {
  menu: ['menu'], kitchen: ['menu'], tables: ['menu'], reservations: ['menu'],
  products: ['store'], bookings: ['bookings'], services: ['bookings'], availability: ['menu', 'bookings'],
  pos: ['menu', 'store'], orders: ['menu', 'store'], cashier: ['menu', 'store'], inventory: ['menu', 'store'],
  shipping: ['menu', 'store'], reports: ['menu', 'store'],
}
const pageFitsServices = (key, services) => !SERVICE_PAGES[key] || SERVICE_PAGES[key].some(s => services.includes(s))

function EmployeesPage() {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [formData, setFormData] = useState(emptyForm)
  const [passwordVisible, setPasswordVisible] = useState({ password: false, confirm: false })
  const [seats, setSeats] = useState(null)
  const { branch, businessType } = useAdminBusiness()
  const services = servicesOf(branch?.services, businessType)
  const pageChoices = PAGES.filter(([key]) => pageFitsServices(key, services))

  useEffect(() => {
    loadEmployees()
  }, [])

  const loadEmployees = async () => {
    try {
      setLoading(true)
      const response = await employeesAPI.getEmployees()
      if (response.data.success) {
        setEmployees(response.data.employees)
        setSeats(response.data.seats || null)
      }
    } catch (error) {
      toast.error(t("Failed to load employees"))
      console.error('Load employees error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee)
      setFormData({
        username: employee.username,
        password: '',
        confirm_password: '',
        full_name: employee.full_name,
        role: employee.role,
        role_choice: employee.role === 'staff' ? 'other' : employee.role,
        role_label: employee.role_label || '',
        permissions: employee.permissions || [],
        pin: '',
        salary: employee.salary || '',
        phone: employee.phone || '',
        email: employee.email || '',
        hire_date: employee.hire_date || new Date().toISOString().split('T')[0],
        is_active: employee.is_active
      })
    } else {
      setEditingEmployee(null)
      setFormData(emptyForm())
    }
    setPasswordVisible({ password: false, confirm: false })
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingEmployee(null)
    setFormData(emptyForm())
    setPasswordVisible({ password: false, confirm: false })
  }

  // Built-in roles keep their own pages; ready-made roles and "Other" are
  // saved as "staff" with a name and a list of pages.
  const chooseRole = choice => {
    const preset = ROLE_PRESETS.find(p => `preset:${p.id}` === choice)
    if (preset) return setFormData(f => ({ ...f, role_choice: choice, role: 'staff', role_label: t(preset.label), permissions: preset.pages.filter(page => pageChoices.some(([key]) => key === page)) }))
    if (choice === 'other') return setFormData(f => ({ ...f, role_choice: choice, role: 'staff', role_label: f.role === 'staff' ? f.role_label : '', permissions: f.role === 'staff' ? f.permissions : [] }))
    setFormData(f => ({ ...f, role_choice: choice, role: choice, role_label: '', permissions: [] }))
  }
  const togglePage = key => setFormData(f => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter(p => p !== key) : [...f.permissions, key] }))

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.username.trim() || !formData.full_name.trim()) {
      toast.error(t("Please fill in all required fields"))
      return
    }

    if (!editingEmployee && !formData.password.trim()) {
      toast.error(t("Password is required for new employees"))
      return
    }

    if (formData.password && formData.password !== formData.confirm_password) {
      toast.error(t('Passwords do not match'))
      return
    }

    try {
      setLoading(true)
      
      if (formData.role === 'staff' && !formData.role_label.trim()) { toast.error(t("Give this role a name")); setLoading(false); return }
      if (formData.role === 'staff' && !formData.permissions.length) { toast.error(t("Choose at least one page this role can open")); setLoading(false); return }
      const { role_choice, ...submitData } = formData
      delete submitData.confirm_password
      if (submitData.role !== 'staff') { delete submitData.role_label; delete submitData.permissions }
      if (editingEmployee && !submitData.password) {
        delete submitData.password
      }
      // PINs are stored hashed; blank keeps the current one.
      if (!submitData.pin) delete submitData.pin

      if (editingEmployee) {
        await employeesAPI.updateEmployee(editingEmployee.id, submitData)
        toast.success(t("Employee updated successfully"))
      } else {
        await employeesAPI.createEmployee(submitData)
        toast.success(t("Employee created successfully"))
      }

      await loadEmployees()
      handleCloseModal()
    } catch (error) {
      toast.error(t(error.response?.data?.error || 'Failed to save employee'))
      console.error('Save employee error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async (employee) => {
    const newStatus = !employee.is_active
    const action = newStatus ? 'activate' : 'deactivate'
    
    if (!window.confirm(t("Are you sure you want to {action} {full_name}?", { action: t(action), full_name: employee.full_name }))) {
      return
    }

    try {
      setLoading(true)
      const updateData = {
        ...employee,
        is_active: newStatus
      }
      await employeesAPI.updateEmployee(employee.id, updateData)
      toast.success(t(newStatus ? "Employee activated successfully" : "Employee deactivated successfully"))
      await loadEmployees()
    } catch (error) {
      toast.error(t(error.response?.data?.error || `Failed to ${action} employee`))
      console.error('Toggle status error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (employee) => {
    if (!window.confirm(t("Are you sure you want to permanently delete {full_name}? This action cannot be undone.", { full_name: employee.full_name }))) {
      return
    }

    try {
      setLoading(true)
      await employeesAPI.deleteEmployee(employee.id)
      toast.success(t("Employee deleted successfully"))
      await loadEmployees()
    } catch (error) {
      toast.error(t(error.response?.data?.error || 'Failed to delete employee'))
      console.error('Delete employee error:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (emp.role_label || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = filterRole === 'all' || emp.role === filterRole
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && emp.is_active) ||
                         (filterStatus === 'inactive' && !emp.is_active)
    return matchesSearch && matchesRole && matchesStatus
  })

  const seatsFull = !!(seats?.max && seats.used >= seats.max)

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 border-purple-200',
      manager: 'bg-blue-100 text-blue-800 border-blue-200',
      cashier: 'bg-green-100 text-green-800 border-green-200',
      kitchen: 'bg-orange-100 text-orange-800 border-orange-200',
      waiter: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      staff: 'bg-sky-100 text-sky-800 border-sky-200'
    }
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text">{t("Employee Management")}</h1>
          <p className="text-gray-600 mt-2">{t("Manage your team members and their roles")}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary disabled:opacity-50"
            disabled={seatsFull}
          >
            <PlusIcon className="h-5 w-5 me-2" />
            {t("Add Employee")}
          </button>
          {seats?.max && (
            <p className={`text-xs ${seatsFull ? 'text-red-600' : 'text-gray-500'}`}>
              {t("{used} of {max} users on your plan (you included)", { used: seats.used, max: seats.max })}
              {seatsFull && <> · <Link to="/admin/billing" className="text-blue-700 hover:underline">{t("Upgrade")}</Link></>}
            </p>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">{employees.length}</div>
            <div className="text-sm text-gray-600">{t("Total Employees")}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {employees.filter(e => e.is_active).length}
            </div>
            <div className="text-sm text-gray-600">{t("Active")}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <XCircleIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {employees.filter(e => !e.is_active).length}
            </div>
            <div className="text-sm text-gray-600">{t("Inactive")}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-purple-600 font-bold text-[10px]"><CurrencyCode /></span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {employees.filter(e => e.salary).length}
            </div>
            <div className="text-sm text-gray-600">{t("With Salary")}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t("Search by name, username, or role...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input ps-10"
              />
            </div>

            {/* Role Filter */}
            <div>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="form-input"
              >
                <option value="all">{t("All Roles")}</option>
                <option value="admin">{t("Admin")}</option>
                <option value="manager">{t("Manager")}</option>
                <option value="cashier">{t("Cashier")}</option>
                <option value="kitchen">{t("Kitchen")}</option>
                <option value="waiter">{t("Waiter")}</option>
                <option value="staff">{t("Other roles")}</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-input"
              >
                <option value="all">{t("All Status")}</option>
                <option value="active">{t("Active")}</option>
                <option value="inactive">{t("Inactive")}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Employees Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Employee")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Role")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Contact")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Salary")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Status")}
                  </th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="loading-spinner mx-auto mb-2"></div>
                      <p className="text-gray-600">{t("Loading employees...")}</p>
                    </td>
                  </tr>
                ) : filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">{t("No employees found")}</p>
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr key={employee.id} className={`hover:bg-gray-50 ${!employee.is_active ? 'bg-gray-50 opacity-75' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {employee.full_name}
                            {!employee.is_active && (
                              <span className="ms-2 text-xs text-gray-500">{t("(Inactive)")}</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">@{employee.username}</div>
                          {employee.has_pin && (
                            <div className="text-xs text-gray-400">{t("PIN set")}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${getRoleBadgeColor(employee.role)}`}>
                          {employee.role === 'staff' ? employee.role_label : t(ROLE_NAMES[employee.role] || employee.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.phone || '-'}</div>
                        <div className="text-sm text-gray-500">{employee.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {employee.salary ? <>{parseFloat(employee.salary).toFixed(2)} <CurrencyCode /></> : '-'}
                        </div>
                        {employee.hire_date && (
                          <div className="text-xs text-gray-500">
                            {t("Since")} {new Date(employee.hire_date).toLocaleDateString(locale())}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {employee.is_active ? (
                          <span className="badge badge-success">{t("Active")}</span>
                        ) : (
                          <span className="badge badge-danger">{t("Inactive")}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleOpenModal(employee)}
                          className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                          title={t("Edit")}
                          disabled={loading}
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(employee)}
                          className={employee.is_active ? 
                            "text-orange-600 hover:text-orange-900 disabled:text-gray-400 disabled:cursor-not-allowed" : 
                            "text-green-600 hover:text-green-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                          }
                          title={employee.is_active ? t("Deactivate") : t("Activate")}
                          disabled={loading}
                        >
                          {employee.is_active ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => handleDelete(employee)}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                          title={t("Delete Permanently")}
                          disabled={loading}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingEmployee ? t("Edit Employee") : t("Add New Employee")}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Basic Information")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">{t("Full Name *")}</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="form-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">{t("Username *")}</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="form-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">
                      {t("Password")} {!editingEmployee && '*'}
                    </label>
                    <div className="relative">
                      <input type={passwordVisible.password ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="form-input pe-12" placeholder={editingEmployee ? t("Leave blank to keep current") : ''} autoComplete="new-password" required={!editingEmployee} />
                      <button type="button" onClick={() => setPasswordVisible(v => ({ ...v, password: !v.password }))} aria-label={t(passwordVisible.password ? 'Hide password' : 'Show password')} aria-pressed={passwordVisible.password}
                        className="absolute inset-y-0 end-0 grid w-12 cursor-pointer place-items-center rounded-e-lg text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                        {passwordVisible.password ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">{t('Confirm password')} {!editingEmployee && '*'}</label>
                    <div className="relative">
                      <input type={passwordVisible.confirm ? 'text' : 'password'} value={formData.confirm_password} onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                        className={`form-input pe-12 ${formData.confirm_password && formData.password !== formData.confirm_password ? 'border-red-400 focus:ring-red-400' : ''}`}
                        placeholder={editingEmployee ? t('Confirm the new password') : t('Enter the password again')} autoComplete="new-password" required={!editingEmployee || !!formData.password} aria-invalid={!!formData.confirm_password && formData.password !== formData.confirm_password} />
                      <button type="button" onClick={() => setPasswordVisible(v => ({ ...v, confirm: !v.confirm }))} aria-label={t(passwordVisible.confirm ? 'Hide password' : 'Show password')} aria-pressed={passwordVisible.confirm}
                        className="absolute inset-y-0 end-0 grid w-12 cursor-pointer place-items-center rounded-e-lg text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                        {passwordVisible.confirm ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                    {formData.confirm_password && formData.password !== formData.confirm_password && <p className="mt-1 text-sm font-medium text-red-600" role="alert">{t('Passwords do not match')}</p>}
                  </div>

                  <div>
                    <label className="form-label">{t("Role *")}</label>
                    <select
                      value={formData.role_choice || formData.role}
                      onChange={(e) => chooseRole(e.target.value)}
                      className="form-input"
                      required
                    >
                      {['cashier', 'waiter', 'kitchen', 'manager', 'admin'].map(role => <option key={role} value={role}>{t(ROLE_NAMES[role])}</option>)}
                      <optgroup label={t("More roles")}>
                        {ROLE_PRESETS.map(preset => <option key={preset.id} value={`preset:${preset.id}`}>{t(preset.label)}</option>)}
                      </optgroup>
                      <option value="other">{t("Other (choose a name and pages)")}</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">{t("PIN (4-6 digits)")}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength="6"
                      value={formData.pin}
                      onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                      className="form-input"
                      placeholder={editingEmployee?.has_pin ? t("Leave blank to keep current") : "1234"}
                    />
                  </div>

                  <div>
                    <label className="form-label">{t("Hire Date")}</label>
                    <input
                      type="date"
                      value={formData.hire_date}
                      onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* What this person can open */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{t("Access")}</h3>
                {formData.role === 'staff' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="form-label" htmlFor="role_label">{t("Role name *")}</label>
                      <input id="role_label" type="text" maxLength={60} value={formData.role_label}
                        onChange={(e) => setFormData({ ...formData, role_label: e.target.value })}
                        className="form-input" placeholder={t("e.g. Delivery driver")} />
                    </div>
                    <fieldset>
                      <legend className="form-label">{t("Pages this role can open *")}</legend>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {pageChoices.map(([key, label]) => (
                          <label key={key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${formData.permissions.includes(key) ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}`}>
                            <input type="checkbox" checked={formData.permissions.includes(key)} onChange={() => togglePage(key)} className="rounded text-blue-600 focus:ring-blue-500" />
                            {t(label)}
                          </label>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-gray-500">{t("Settings, website design and billing stay with the owner and admins.")}</p>
                    </fieldset>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    {formData.role === 'admin'
                      ? t("Admins can open everything, including settings.")
                      : <>{t("Can open:")} {(ROLE_PAGES[formData.role] || []).filter(key => pageChoices.some(([k]) => k === key)).map(key => t(PAGES.find(([k]) => k === key)?.[1] || key)).join(t(", "))}</>}
                  </p>
                )}
              </div>

              {/* Contact & Compensation */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Contact & Compensation")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">{t("Phone")}</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="form-input"
                      placeholder={t("+968 9XXX XXXX")}
                    />
                  </div>

                  <div>
                    <label className="form-label">{t("Email")}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-input"
                      placeholder={t("employee@example.com")}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="form-label">{t("Monthly salary")} (<CurrencyCode />)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              {editingEmployee && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Account Status")}</h3>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="sr-only"
                        />
                        <div className={`w-10 h-6 rounded-full transition-colors ${
                          formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <div className={`absolute start-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                          formData.is_active ? 'transform translate-x-4' : ''
                        }`}></div>
                      </div>
                      <span className="ms-3 text-sm font-medium text-gray-900">
                        {formData.is_active ? t("Active") : t("Inactive")}
                      </span>
                    </label>
                    <span className="text-sm text-gray-500">
                      {formData.is_active ? 
                        t("Employee can access the system") : 
                        t("Employee cannot access the system")
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                  disabled={loading}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? t("Saving...") : editingEmployee ? t("Update Employee") : t("Add Employee")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmployeesPage
